import { parsePublishableKey } from "@clerk/shared/keys";
import { BASE_ROUTE } from "@remi/constants";
import { getEnvPublic } from "@remi/environment";
import { remiLogger } from "@remi/logging";

interface ClerkOAuthOrigins {
  fapi: string | null;
  accounts: string | null;
}

// Production FAPI hosts are clerk.<domain> with hosted accounts at accounts.<domain>;
// dev-instance FAPI hosts are <slug>.clerk.accounts.dev with hosted accounts at <slug>.accounts.dev.
const deriveAccountsOrigin = (hostname: string): string | null => {
  if (hostname.startsWith("clerk.")) {
    return `https://accounts.${hostname.slice("clerk.".length)}`;
  }
  if (hostname.endsWith(".clerk.accounts.dev")) {
    return `https://${hostname.replace(".clerk.accounts.dev", ".accounts.dev")}`;
  }
  return null;
};

const getClerkOAuthOrigins = (): ClerkOAuthOrigins => {
  try {
    const parsed = parsePublishableKey(getEnvPublic("NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY"));
    if (!parsed?.frontendApi) {
      return { fapi: null, accounts: null };
    }

    const fapi = `https://${parsed.frontendApi}`;
    const hostname = new URL(fapi).hostname;
    return { fapi, accounts: deriveAccountsOrigin(hostname) };
  } catch (error) {
    remiLogger.warn("Failed to derive Clerk FAPI origin from NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY", {
      error: String(error),
    });
    return { fapi: null, accounts: null };
  }
};

const CLERK_OAUTH_ORIGINS = getClerkOAuthOrigins();

const isClerkOAuthContinuation = (url: URL): boolean => {
  // Clerk FAPI owns protocol paths; hosted accounts is trusted only for consent.
  if (CLERK_OAUTH_ORIGINS.fapi !== null && url.origin === CLERK_OAUTH_ORIGINS.fapi) {
    return url.pathname.startsWith("/oauth/");
  }
  if (CLERK_OAUTH_ORIGINS.accounts !== null && url.origin === CLERK_OAUTH_ORIGINS.accounts) {
    return url.pathname === "/oauth-consent";
  }
  return false;
};

const isEncodedAbsoluteUrl = (value: string): boolean => {
  const lowerValue = value.toLowerCase();
  return lowerValue.startsWith("http%3a%2f%2f") || lowerValue.startsWith("https%3a%2f%2f");
};

const parseRedirectUrl = (redirectQueryParam: string): URL => {
  const url = new URL(redirectQueryParam, window.location.origin);
  if (isEncodedAbsoluteUrl(redirectQueryParam)) {
    return new URL(decodeURIComponent(redirectQueryParam), window.location.origin);
  }
  if (
    url.origin === window.location.origin &&
    (url.pathname.startsWith("/%2F") ||
      url.pathname.startsWith("/http%3A%2F%2F") ||
      url.pathname.startsWith("/https%3A%2F%2F"))
  ) {
    const decoded = new URL(decodeURIComponent(redirectQueryParam), window.location.origin);
    if (decoded.origin === window.location.origin) {
      return decoded;
    }
    return new URL(BASE_ROUTE, window.location.origin);
  }
  return url;
};

export type RedirectTarget = { kind: "internal"; path: string } | { kind: "clerk-oauth"; url: string };

export const getSafeRedirectTarget = (redirectQueryParam: string | null): RedirectTarget => {
  if (!redirectQueryParam) {
    return { kind: "internal", path: BASE_ROUTE };
  }
  try {
    const url = parseRedirectUrl(redirectQueryParam);
    if (url.origin !== window.location.origin) {
      if (isClerkOAuthContinuation(url)) {
        return { kind: "clerk-oauth", url: url.toString() };
      }
      return { kind: "internal", path: BASE_ROUTE };
    }
    // Prevent redirect loops back to the sign-in page
    if (url.pathname.startsWith("/sign-in")) {
      return { kind: "internal", path: BASE_ROUTE };
    }
    return { kind: "internal", path: `${url.pathname}${url.search}${url.hash}` };
  } catch {
    return { kind: "internal", path: BASE_ROUTE };
  }
};
